import { PeOrderDetail } from 'model/modules/pe/orders/peOrderDetail';
import mapPeCompanyOrderDetailFromApi from 'rtkQuery/sharespro/mapper/modules/pe/companyOrders/mapPeCompanyOrderDetailFromApi';
import mapPeDealDetailFromApi from 'rtkQuery/sharespro/mapper/modules/pe/deals/mapPeDealDetailFromApi';
import mapPeDealListFromApi from 'rtkQuery/sharespro/mapper/modules/pe/deals/mapPeDealListFromApi';
import mapPeOrderDetailFromApi from 'rtkQuery/sharespro/mapper/modules/pe/orders/mapPeOrderDetailFromApi';
import mapPeOrderListFromApi from 'rtkQuery/sharespro/mapper/modules/pe/orders/mapPeOrderListFromApi';
import sharesproApi from 'rtkQuery/sharespro/sharesproApiService';
import { SharesproApiResponse } from 'rtkQuery/sharespro/types/sharesproApiResponse';
import mapPeRecommendationsFromApi from 'rtkQuery/sharespro/mapper/modules/pe/mapPeRecommendationsFromApi';
import { PeOrderListItem } from 'model/modules/pe/orders/peOrderList';
import { ApiCreatePeCompanyOrderRequestData } from 'rtkQuery/sharespro/types/pe/orders/createPeCompanyOrder';
import {
  ApiFetchPeDealsQueryParams,
  ApiPeDealListItem,
} from 'rtkQuery/sharespro/types/pe/deals/fetchPeDeals';
import mapPeCompanyDetailFromApi from 'rtkQuery/sharespro/mapper/modules/pe/peCompany/mapPeCompanyDetailFromApi';
import mapOrderBookFromApi from 'rtkQuery/sharespro/mapper/shared/mapOrderBookFromApi';
import mapPeOffersFromApi from 'rtkQuery/sharespro/mapper/modules/pe/mapPeOfferFromApi';
import mapPeAssetsFromApi from 'rtkQuery/sharespro/mapper/modules/pe/mapPeAssetsFromApi';

const cancelPeOrderListMapper =
  (id: ID) =>
  (item: PeOrderListItem): PeOrderListItem => {
    return item.id === id
      ? {
          ...item,
          isAllowCancel: false,
        }
      : item;
  };

export const peServiceApi = sharesproApi.injectEndpoints({
  endpoints: (builder) => ({
    fetchPeSelfOrders: builder.query<
      PaginationList<PeOrderListItem>,
      ApiPaginationQueryParams
    >({
      query: (params) => ({
        url: '/v3/finance/pe/order/list',
        method: 'GET',
        sendToken: true,
        params,
      }),
      providesTags: ['PeOrders'],
      serializeQueryArgs: ({ endpointName }) => {
        return endpointName;
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg?.page !== previousArg?.page;
      },
      transformResponse(
        baseQueryReturnValue: SharesproApiResponse<
          ApiPaginationListResponse<ApiPeOrderListItem>
        >
      ) {
        return mapPeOrderListFromApi(baseQueryReturnValue);
      },
    }),
    fetchPeOrderDetail: builder.query<
      PeOrderDetail,
      ApiFetchPeAssetDetailRequestData
    >({
      query: (params) => ({
        url: '/v3/finance/pe/order/detail',
        method: 'GET',
        sendToken: true,
        params,
      }),
      providesTags: (result, error, arg) => [
        { type: 'PeOrderDetail', id: arg.id },
      ],
      transformResponse(
        baseQueryReturnValue: SharesproApiResponse<ApiPeOrderDetail>
      ) {
        return mapPeOrderDetailFromApi(baseQueryReturnValue.data);
      },
    }),
    fetchPeCompanyOrderDetail: builder.query<
      PeCompanyOrderDetail,
      ApiFetchPeAssetDetailRequestData
    >({
      query: (params) => ({
        url: '/v3/finance/pe/company/detail',
        method: 'GET',
        sendToken: true,
        params,
      }),
      transformResponse(
        baseQueryReturnValue: SharesproApiResponse<ApiPeCompanyOrderDetail>
      ) {
        return mapPeCompanyOrderDetailFromApi(baseQueryReturnValue.data);
      },
    }),
    fetchPeDeals: builder.query<
      PaginationList<PeDealListItem>,
      ApiFetchPeDealsQueryParams
    >({
      query: (params) => ({
        url: '/v3/finance/pe/asset/list',
        method: 'GET',
        sendToken: true,
        params,
      }),
      transformResponse(
        baseQueryReturnValue: SharesproApiResponse<
          ApiPaginationListResponse<ApiPeDealListItem>
        >
      ) {
        return mapPeDealListFromApi(baseQueryReturnValue);
      },
    }),
    fetchPeDealDetail: builder.query<
      PeDealDetail,
      ApiFetchPeAssetDetailRequestData
    >({
      query: (params) => ({
        url: '/v3/finance/pe/asset/detail',
        method: 'GET',
        sendToken: true,
        params,
      }),
      transformResponse(
        baseQueryReturnValue: SharesproApiResponse<ApiPeDealDetail>
      ) {
        return mapPeDealDetailFromApi(baseQueryReturnValue.data);
      },
    }),
    fetchPeCompanyDetail: builder.query<
      PeCompanyDetail,
      ApiFetchPeCompanyDetailRequestData
    >({
      query: (params) => ({
        url: '/v3/finance/pe/company/company',
        method: 'GET',
        sendToken: true,
        params,
      }),
      transformResponse(
        baseQueryReturnValue: SharesproApiResponse<ApiPeCompanyFullData>
      ) {
        return mapPeCompanyDetailFromApi(baseQueryReturnValue.data);
      },
    }),
    createPeOrder: builder.mutation<
      SharesproApiResponse<void>,
      ApiCreatePeOrderRequestData
    >({
      query: (data) => ({
        url: '/v3/finance/pe/order/lead',
        method: 'POST',
        sendToken: true,
        body: data,
      }),
    }),
    createPeCompanyOrder: builder.mutation<
      SharesproApiResponse<void>,
      ApiCreatePeCompanyOrderRequestData
    >({
      query: (data) => ({
        url: '/v3/finance/pe/order/create',
        method: 'POST',
        sendToken: true,
        body: data,
      }),
      invalidatesTags: ['PeOrders'],
    }),
    cancelPeOrder: builder.mutation<SharesproApiResponse<void>, ID>({
      query: (id) => ({
        url: '/v3/finance/pe/order/cancel',
        method: 'DELETE',
        sendToken: true,
        params: { id },
      }),
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            peServiceApi.util.updateQueryData(
              'fetchPeSelfOrders',
              { page: 1 },
              (draft) => {
                draft.items = draft.items.map<PeOrderListItem>(
                  cancelPeOrderListMapper(id)
                );
              }
            )
          );
        } catch {}
      },
      invalidatesTags: (result, error, id) => [{ type: 'PeOrderDetail', id }],
    }),
    cancelPeOrderDetail: builder.mutation<
      SharesproApiResponse<void>,
      CancelPeOrderDetailRequestData
    >({
      query: (data) => ({
        url: '/v3/finance/pe/order/cancel',
        method: 'DELETE',
        sendToken: true,
        params: { id: data.orderId },
      }),
      async onQueryStarted(data, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            peServiceApi.util.updateQueryData(
              'fetchPeCompanyDetail',
              { id: data.assetId },
              (draft) => {
                draft.investorsOrders =
                  draft.investorsOrders.map<PeOrderListItem>(
                    cancelPeOrderListMapper(data.orderId)
                  );
              }
            )
          );
          dispatch(
            peServiceApi.util.updateQueryData(
              'fetchPeDealDetail',
              { id: data.assetId },
              (draft) => {
                draft.company.investorsOrders =
                  draft.company.investorsOrders.map<PeOrderListItem>(
                    cancelPeOrderListMapper(data.orderId)
                  );
              }
            )
          );
          dispatch(
            peServiceApi.util.updateQueryData(
              'fetchPeCompanyOrderDetail',
              { id: data.assetId },
              (draft) => {
                draft.company.investorsOrders =
                  draft.company.investorsOrders.map<PeOrderListItem>(
                    cancelPeOrderListMapper(data.orderId)
                  );
              }
            )
          );
          dispatch(
            peServiceApi.util.updateQueryData(
              'fetchPeOrderDetail',
              { id: data.assetId },
              (draft) => {
                draft.company.investorsOrders =
                  draft.company.investorsOrders.map<PeOrderListItem>(
                    cancelPeOrderListMapper(data.orderId)
                  );
              }
            )
          );
        } catch {}
      },
      invalidatesTags: (result, error, data) => [
        { type: 'PeOrderDetail', id: data.orderId },
      ],
    }),
    fetchPeOffers: builder.query<PeRecommendations, void>({
      query: () => ({
        url: '/v3/finance/recommendation/pe',
        method: 'GET',
        sendToken: true,
      }),
      transformResponse(
        baseQueryReturnValue: SharesproApiResponse<ApiFetchPeRecommendationsResponse>
      ) {
        return mapPeRecommendationsFromApi(baseQueryReturnValue.data);
      },
    }),
    fetchPeOrderBook: builder.query<OrderBook, ID>({
      query: (companyId) => ({
        url: '/v3/finance/pe/order/book',
        method: 'GET',
        sendToken: true,
        params: { company_id: companyId },
      }),
      transformResponse(
        baseQueryReturnValue: SharesproApiResponse<ApiOrderBook>
      ) {
        return mapOrderBookFromApi(baseQueryReturnValue.data);
      },
    }),
    fetchPeOffersV2: builder.query<PeOffers, void>({
      query: () => ({
        url: '/v3/finance/pe/store/offers',
        method: 'GET',
        sendToken: true,
      }),
      transformResponse(
        baseQueryReturnValue: SharesproApiResponse<ApiPeOffers>
      ) {
        return mapPeOffersFromApi(baseQueryReturnValue.data);
      },
    }),
    fetchPeAssets: builder.query<
      RemoteList<PeOfferListItem>,
      ApiPeAssetQueryParams
    >({
      query: (params) => ({
        url: '/v3/finance/pe/store/assets',
        method: 'GET',
        sendToken: true,
        params,
      }),
      transformResponse(
        baseQueryReturnValue: SharesproApiResponse<ApiPeAssets>
      ) {
        return mapPeAssetsFromApi(baseQueryReturnValue.data);
      },
    }),
  }),
});

export const {
  useFetchPeSelfOrdersQuery,
  useFetchPeOrderDetailQuery,
  useFetchPeCompanyOrderDetailQuery,
  useFetchPeDealsQuery,
  useFetchPeDealDetailQuery,
  useFetchPeCompanyDetailQuery,
  useCreatePeOrderMutation,
  useCreatePeCompanyOrderMutation,
  useCancelPeOrderMutation,
  useFetchPeOffersQuery,
  useFetchPeOrderBookQuery,
  useFetchPeOffersV2Query,
  useFetchPeAssetsQuery,
  useCancelPeOrderDetailMutation,
} = peServiceApi;
